import { SSMT } from '../../../constants/qnames';

/**
 * Shared business rules for Role 4 (Directors Report).
 *
 * Both role4-120000-directors-report.component.ts AND the pure
 * role4DirectorsReportValidator import from this file so the dynamic
 * director-visibility, identification-type pattern, and required-field
 * logic stay DRY.
 *
 * Modify rules HERE only — anywhere else risks the parity-check harness
 * throwing in dev because the two paths disagree.
 */

export const ROLE4_NUMBER_OF_DIRECTORS_KEY =
  SSMT.NumberOfDirectorsSigningDirectorsReport;

/**
 * Sentinel for "no director count is selected" — ZERO directors are visible.
 *
 * THE single definition, imported by the component, the pure validator and the
 * submit projector. An absent, empty or unparseable count resolves here, so all
 * three agree that nothing director-scoped is visible, required, or submittable.
 *
 * Superseded the old `ROLE4_DEFAULT_NUMBER_OF_DIRECTORS = 2` fallback: a count
 * the user cleared (or that extraction never produced) is NOT an implicit "2",
 * and treating it as one left directors 1–2 rendered-nowhere but still present
 * in state and in the submitted payload.
 */
export const ROLE4_NO_DIRECTOR_COUNT_SELECTED = 0;

/**
 * Identification types that trigger the 12-digit pattern requirement on a
 * director's IdentificationNumber field. Other types (Passport, etc.)
 * remove the pattern but keep the required check (when visible).
 */
export const ROLE4_IDENTIFICATION_TYPES_REQUIRING_PATTERN: ReadonlyArray<string> = [
  'MyKad',
  'MyPR',
  'MyKAS',
];

/** Pattern applied to IdentificationNumber when type is in the list above. */
export const ROLE4_IDENTIFICATION_PATTERN = /^[0-9]{12}$/;

/**
 * Case-insensitive membership check used by the component (conditional pattern
 * validator + inline error messages) and the pure validator to decide whether
 * the 12-digit rule applies to a given IdentificationNumber field. Twin of
 * `role5TypeRequiresPattern` in role5-statement-by-directors.rules.ts — keep the
 * two behaviourally identical.
 *
 * Case-INsensitive on purpose: conversion sources still emit the legacy
 * uppercase `MYKAD` / `MYPR` / `MYKAS` codes (role-120000.form.json itself used
 * to list them in `conditionalOn.values`). An exact match read those as a
 * free-text type, so neither the pattern validator nor the "Please input a 12
 * digit Identification number." line was ever produced.
 */
export function role4TypeRequiresPattern(
  typeValue: unknown,
  allowedValues: ReadonlyArray<string> = ROLE4_IDENTIFICATION_TYPES_REQUIRING_PATTERN
): boolean {
  if (typeof typeValue !== 'string' || typeValue.length === 0) return false;
  const normalized = typeValue.trim().toLowerCase();
  return allowedValues.some((allowed) => allowed.toLowerCase() === normalized);
}

/**
 * Per-director qname triplet: type-of-identification, identification-number,
 * and the director ordinal (1..5). Used by the component for valueChanges
 * subscriptions and by the pure validator to walk the same set.
 */
export interface Role4DirectorMapping {
  typeField: string;
  idField: string;
  directorNumber: number;
}

export const ROLE4_DIRECTOR_MAPPINGS: ReadonlyArray<Role4DirectorMapping> = [
  {
    typeField: SSMT.TypeOfIdentificationOfFirstDirectorWhoSignedDirectorsReport,
    idField: SSMT.IdentificationNumberOfFirstDirectorWhoSignedDirectorsReport,
    directorNumber: 1,
  },
  {
    typeField: SSMT.TypeOfIdentificationOfSecondDirectorWhoSignedDirectorsReport,
    idField: SSMT.IdentificationNumberOfSecondDirectorWhoSignedDirectorsReport,
    directorNumber: 2,
  },
  {
    typeField: SSMT.TypeOfIdentificationOfThirdDirectorWhoSignedDirectorsReport,
    idField: SSMT.IdentificationNumberOfThirdDirectorWhoSignedDirectorsReport,
    directorNumber: 3,
  },
  {
    typeField: SSMT.TypeOfIdentificationOfFourthDirectorWhoSignedDirectorsReport,
    idField: SSMT.IdentificationNumberOfFourthDirectorWhoSignedDirectorsReport,
    directorNumber: 4,
  },
  {
    typeField: SSMT.TypeOfIdentificationOfFifthDirectorWhoSignedDirectorsReport,
    idField: SSMT.IdentificationNumberOfFifthDirectorWhoSignedDirectorsReport,
    directorNumber: 5,
  },
];

/**
 * Resolve the effective number of visible directors from raw form data.
 *
 * An absent, empty or unparseable count resolves to
 * ROLE4_NO_DIRECTOR_COUNT_SELECTED (0) — identical to the component's
 * `resolveDirectorCount`, which imports this same function. The earlier
 * "resolve to 2" fallback was a DELIBERATE divergence kept for the pure
 * validator's error map (2026-07-28); it was **removed 2026-07-29** because the
 * submit projector shares this resolver, and a `2` there let directors 1–2 ship
 * in the payload while nothing was rendered on screen.
 *
 * Consequence for the headless path, accepted knowingly: a conversion payload
 * that simply OMITS the count key now reports zero visible directors, so its
 * director fields shed `required` in the error map and are stripped at submit.
 * The blocking verdict is unaffected either way — the count is itself
 * `required: true` in `role-120000.form.json`, so `hasErrors` stays true — and
 * role 4 is not on `PURE_VERDICT_SHEETS`.
 */
export function getVisibleDirectorCount(sheetData: Record<string, any>): number {
  const raw = sheetData[ROLE4_NUMBER_OF_DIRECTORS_KEY];
  if (raw === null || raw === undefined || raw === '') {
    return ROLE4_NO_DIRECTOR_COUNT_SELECTED;
  }
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) ? parsed : ROLE4_NO_DIRECTOR_COUNT_SELECTED;
}
